/**
 * Faux Pas Detection — corpus textuel MVP.
 *
 * Source paradigme : Faux Pas Recognition Test
 * (Stone, Baron-Cohen & Knight 1998, JoCN 10(5):640-656,
 *  doi:10.1162/089892998562942).
 *
 * Le corpus original = 20 vignettes EN (10 faux pas + 10 contrôles). Ici on
 * propose 24 vignettes FR originales (12 faux pas + 12 contrôles), graduées
 * sur 5 tiers de subtilité (1 = bourde flagrante / 5 = bourde très subtile).
 *
 * MVP — vignettes FR seed corpus, target Sprint 2+ : extend to 60+ items
 * + cultural variants Stone-Baron-Cohen 1998.
 *
 * TODO Sprint 2+ : i18n vignettes corpus (n8n batch traduction + revue native).
 */
export const FAUX_PAS_CORPUS = [
    // ============================================================
    // Difficulty 1 — bourdes flagrantes (5 items, 3 fp + 2 ctrl)
    // ============================================================
    {
        id: 'fp-fr-001',
        story: "Sophie offre une boîte de chocolats à son amie Léa pour son anniversaire. Léa ouvre le paquet et la remercie. Sophie lui dit alors : « Je sais que tu détestes les chocolats au lait, mais ceux-ci sont tous au lait. Tu vas voir, ils sont délicieux. »",
        hasFauxPas: true,
        characters: ['Sophie', 'Léa'],
        whoCommitted: 'Sophie',
        whoChoices: ['Sophie', 'Léa', 'Personne'],
        whyChoices: [
            "Sophie reconnaît que Léa déteste ces chocolats mais lui en offre quand même",
            "Léa a refusé le cadeau",
            "Sophie a oublié l'anniversaire",
            "Léa a souri faussement",
        ],
        whyCorrectIdx: 0,
        difficulty: 1,
    },
    {
        id: 'fp-fr-002',
        story: "À la fin d'un dîner, Marc tend son assiette à Claire et lance devant tout le monde : « Ta tarte aux pommes est franchement ratée, je n'arrive pas à la finir. » Claire, qui a passé l'après-midi à la préparer, baisse les yeux.",
        hasFauxPas: true,
        characters: ['Marc', 'Claire'],
        whoCommitted: 'Marc',
        whoChoices: ['Marc', 'Claire', 'Personne'],
        whyChoices: [
            "Marc critique publiquement le travail de Claire devant les invités",
            "Claire a servi un dessert immangeable",
            "Marc a refusé de manger",
            "Claire aurait dû prévenir",
        ],
        whyCorrectIdx: 0,
        difficulty: 1,
    },
    {
        id: 'fp-fr-003',
        story: "Lors d'une réunion d'équipe, Julien dit à voix haute : « De toute façon, tout le monde sait que Karim a été embauché juste pour le quota. » Karim est assis en face de lui. Le silence tombe.",
        hasFauxPas: true,
        characters: ['Julien', 'Karim'],
        whoCommitted: 'Julien',
        whoChoices: ['Julien', 'Karim', 'Personne'],
        whyChoices: [
            "Julien remet en cause publiquement la légitimité de Karim",
            "Karim n'aurait pas dû être présent",
            "Julien a parlé trop bas",
            "Karim a mal réagi",
        ],
        whyCorrectIdx: 0,
        difficulty: 1,
    },
    {
        id: 'fp-fr-c001',
        story: "Au restaurant, le serveur apporte deux plats. Anne dit poliment : « Excusez-moi, j'ai commandé le poisson, pas la viande. » Le serveur s'excuse et repart en cuisine corriger l'erreur.",
        hasFauxPas: false,
        characters: ['Anne', 'Le serveur'],
        whoCommitted: null,
        whoChoices: ['Anne', 'Le serveur', 'Personne'],
        whyChoices: [
            "Anne a humilié le serveur",
            "Le serveur a été grossier",
            "Aucune bourde — Anne demande poliment, le serveur corrige",
            "Anne aurait dû rester silencieuse",
        ],
        whyCorrectIdx: null,
        difficulty: 1,
    },
    {
        id: 'fp-fr-c002',
        story: "Paul croise sa voisine Léa dans l'ascenseur. Il la salue et lui demande comment se passe sa rentrée. Léa répond brièvement qu'elle est fatiguée. Paul hoche la tête, lui souhaite un bon repos et sort à son étage.",
        hasFauxPas: false,
        characters: ['Paul', 'Léa'],
        whoCommitted: null,
        whoChoices: ['Paul', 'Léa', 'Personne'],
        whyChoices: [
            "Paul a été indiscret",
            "Léa aurait dû en dire plus",
            "Aucune bourde — l'échange est neutre et poli",
            "Paul a parlé trop fort",
        ],
        whyCorrectIdx: null,
        difficulty: 1,
    },
    // ============================================================
    // Difficulty 2 — bourdes nettes mais moins criantes (5 items, 2 fp + 3 ctrl)
    // ============================================================
    {
        id: 'fp-fr-101',
        story: "Élise rencontre une ancienne collègue, Marion, dans la rue. Elle s'exclame : « Félicitations pour ta grossesse ! » Marion la regarde, gênée, et répond doucement qu'elle n'est pas enceinte.",
        hasFauxPas: true,
        characters: ['Élise', 'Marion'],
        whoCommitted: 'Élise',
        whoChoices: ['Élise', 'Marion', 'Personne'],
        whyChoices: [
            "Élise a présumé une grossesse alors que Marion ne l'était pas",
            "Marion aurait dû le dire avant",
            "Élise a parlé trop bas",
            "Marion a mal pris une bonne nouvelle",
        ],
        whyCorrectIdx: 0,
        difficulty: 2,
    },
    {
        id: 'fp-fr-102',
        story: "Au repas de famille, Hugo raconte avec entrain comment son père lui a appris à pêcher. Il se tourne vers son cousin Tom et ajoute : « Toi tu n'as jamais connu ça, t'as eu de la chance. » Le père de Tom est mort quand Tom avait six ans.",
        hasFauxPas: true,
        characters: ['Hugo', 'Tom'],
        whoCommitted: 'Hugo',
        whoChoices: ['Hugo', 'Tom', 'Personne'],
        whyChoices: [
            "Hugo qualifie de « chance » l'absence du père de Tom, qui est mort",
            "Tom a fait une scène",
            "Hugo a parlé de pêche au mauvais moment",
            "Tom n'aurait pas dû venir",
        ],
        whyCorrectIdx: 0,
        difficulty: 2,
    },
    {
        id: 'fp-fr-c101',
        story: "À la pharmacie, le pharmacien demande à Sarah si elle a déjà pris ce médicament. Sarah confirme. Il lui rappelle de ne pas dépasser la dose et lui souhaite bonne journée.",
        hasFauxPas: false,
        characters: ['Sarah', 'Le pharmacien'],
        whoCommitted: null,
        whoChoices: ['Sarah', 'Le pharmacien', 'Personne'],
        whyChoices: [
            "Le pharmacien a été intrusif",
            "Sarah a menti",
            "Aucune bourde — un échange professionnel standard",
            "Sarah aurait dû refuser le conseil",
        ],
        whyCorrectIdx: null,
        difficulty: 2,
    },
    {
        id: 'fp-fr-c102',
        story: "En réunion, Camille présente sa proposition. Son manager pose deux questions précises sur le calendrier et le budget. Camille répond, prend note d'un point à approfondir, et la réunion se poursuit.",
        hasFauxPas: false,
        characters: ['Camille', 'Le manager'],
        whoCommitted: null,
        whoChoices: ['Camille', 'Le manager', 'Personne'],
        whyChoices: [
            "Le manager a humilié Camille",
            "Camille a esquivé la question",
            "Aucune bourde — questions managériales normales",
            "Camille aurait dû refuser de répondre",
        ],
        whyCorrectIdx: null,
        difficulty: 2,
    },
    {
        id: 'fp-fr-c103',
        story: "Dans la cour de l'école, deux enfants se disputent un ballon. La maîtresse intervient calmement, leur rappelle la règle du tour de jeu, et leur propose de chronométrer ensemble. Les deux enfants acceptent.",
        hasFauxPas: false,
        characters: ['La maîtresse', "Les enfants"],
        whoCommitted: null,
        whoChoices: ['La maîtresse', "Les enfants", 'Personne'],
        whyChoices: [
            "La maîtresse a été autoritaire",
            "Les enfants ont été punis injustement",
            "Aucune bourde — résolution éducative apaisée",
            "Le ballon n'aurait pas dû être autorisé",
        ],
        whyCorrectIdx: null,
        difficulty: 2,
    },
    // ============================================================
    // Difficulty 3 — bourdes plausibles, contexte requis (5 items, 3 fp + 2 ctrl)
    // ============================================================
    {
        id: 'fp-fr-201',
        story: "Lors d'un pot de départ, Nadia félicite Vincent à voix haute : « C'est génial que tu changes enfin de poste, tu as l'air tellement plus heureux. » Mais Vincent n'a pas démissionné — il vient d'être licencié. Il sourit faiblement.",
        hasFauxPas: true,
        characters: ['Nadia', 'Vincent'],
        whoCommitted: 'Nadia',
        whoChoices: ['Nadia', 'Vincent', 'Personne'],
        whyChoices: [
            "Nadia présume un choix volontaire alors que Vincent a été licencié",
            "Vincent a caché son départ",
            "Nadia a parlé trop fort",
            "Vincent aurait dû refuser le pot",
        ],
        whyCorrectIdx: 0,
        difficulty: 3,
    },
    {
        id: 'fp-fr-202',
        story: "Un médecin reçoit Mme Fontaine pour ses résultats. Il commence par : « Bon, j'ai vu pire ce mois-ci, alors ne vous inquiétez pas trop. » Mme Fontaine, qui a passé deux semaines anxieuse, sent son cœur s'accélérer.",
        hasFauxPas: true,
        characters: ['Le médecin', 'Mme Fontaine'],
        whoCommitted: 'Le médecin',
        whoChoices: ['Le médecin', 'Mme Fontaine', 'Personne'],
        whyChoices: [
            "Le médecin compare avec d'autres patients au lieu de rassurer la patiente",
            "Mme Fontaine est trop anxieuse",
            "Le médecin a été trop bref",
            "Mme Fontaine aurait dû demander avant",
        ],
        whyCorrectIdx: 0,
        difficulty: 3,
    },
    {
        id: 'fp-fr-203',
        story: "Pendant un dîner d'anciens élèves, Romain raconte combien il déteste son nouvel appartement. Il ajoute : « Franchement, qui peut vivre dans le 19e arrondissement, c'est sinistré. » Sa voisine de table, qu'il n'a pas reconnue, vient justement d'y emménager.",
        hasFauxPas: true,
        characters: ['Romain', 'La voisine de table'],
        whoCommitted: 'Romain',
        whoChoices: ['Romain', 'La voisine de table', 'Personne'],
        whyChoices: [
            "Romain dénigre le quartier sans savoir que sa voisine y vit",
            "La voisine aurait dû se présenter",
            "Romain a parlé trop fort",
            "Le dîner était mal organisé",
        ],
        whyCorrectIdx: 0,
        difficulty: 3,
    },
    {
        id: 'fp-fr-c201',
        story: "Au supermarché, Léon hésite devant les yaourts. Une dame lui propose son rayon préféré et lui explique pourquoi. Léon la remercie, prend un pot, et continue ses courses.",
        hasFauxPas: false,
        characters: ['Léon', 'La dame'],
        whoCommitted: null,
        whoChoices: ['Léon', 'La dame', 'Personne'],
        whyChoices: [
            "La dame a été intrusive",
            "Léon a manqué de politesse",
            "Aucune bourde — micro-conseil bienveillant accepté",
            "Le supermarché aurait dû mieux ranger",
        ],
        whyCorrectIdx: null,
        difficulty: 3,
    },
    {
        id: 'fp-fr-c202',
        story: "Dans le métro bondé, un passager demande à Inès si elle peut se décaler un peu. Inès s'excuse, se déplace, et lui laisse la place pour s'installer. Le passager la remercie.",
        hasFauxPas: false,
        characters: ['Inès', 'Le passager'],
        whoCommitted: null,
        whoChoices: ['Inès', 'Le passager', 'Personne'],
        whyChoices: [
            "Le passager a été agressif",
            "Inès a été dérangée injustement",
            "Aucune bourde — demande civique courante, réponse polie",
            "Le métro aurait dû être moins plein",
        ],
        whyCorrectIdx: null,
        difficulty: 3,
    },
    // ============================================================
    // Difficulty 4 — bourdes subtiles (5 items, 2 fp + 3 ctrl)
    // ============================================================
    {
        id: 'fp-fr-301',
        story: "Lors d'une présentation client, Léa, junior, donne une réponse précise sur le marché. Son manager Pierre la coupe : « Laisse-moi reformuler proprement ce que Léa essaie de dire. » Le client hoche la tête vers Pierre, ignorant Léa.",
        hasFauxPas: true,
        characters: ['Pierre', 'Léa', 'Le client'],
        whoCommitted: 'Pierre',
        whoChoices: ['Pierre', 'Léa', 'Le client'],
        whyChoices: [
            "Pierre s'approprie la parole de Léa et l'efface devant le client",
            "Léa s'est mal exprimée",
            "Le client a été impoli",
            "Pierre a aidé Léa à mieux formuler",
        ],
        whyCorrectIdx: 0,
        difficulty: 4,
    },
    {
        id: 'fp-fr-302',
        story: "Maxime invite des amis à goûter le vin qu'il vient d'acheter. Il sert son ami Tarek et lance avec un grand sourire : « Toi tu vas adorer, c'est un vin pour les vrais connaisseurs. » Tarek, musulman pratiquant, ne boit pas d'alcool — Maxime le sait.",
        hasFauxPas: true,
        characters: ['Maxime', 'Tarek'],
        whoCommitted: 'Maxime',
        whoChoices: ['Maxime', 'Tarek', 'Personne'],
        whyChoices: [
            "Maxime sert et insiste auprès de Tarek alors qu'il sait qu'il ne boit pas",
            "Tarek aurait dû refuser plus tôt",
            "Maxime a choisi un mauvais vin",
            "Tarek n'aurait pas dû venir",
        ],
        whyCorrectIdx: 0,
        difficulty: 4,
    },
    {
        id: 'fp-fr-c301',
        story: "Une enseignante donne un retour à un élève sur sa rédaction. Elle souligne deux passages réussis, puis explique précisément où la structure pourrait être resserrée. L'élève prend des notes et la remercie.",
        hasFauxPas: false,
        characters: ["L'enseignante", "L'élève"],
        whoCommitted: null,
        whoChoices: ["L'enseignante", "L'élève", 'Personne'],
        whyChoices: [
            "L'enseignante a découragé l'élève",
            "L'élève a été humilié",
            "Aucune bourde — feedback équilibré et reçu",
            "L'enseignante aurait dû ne dire que du positif",
        ],
        whyCorrectIdx: null,
        difficulty: 4,
    },
    {
        id: 'fp-fr-c302',
        story: "Un commercial appelle un prospect, se présente en moins de trente secondes, demande s'il dérange. Le prospect dit ne pas être disponible. Le commercial propose de rappeler à un horaire qui convient, le prospect choisit, et ils raccrochent.",
        hasFauxPas: false,
        characters: ['Le commercial', 'Le prospect'],
        whoCommitted: null,
        whoChoices: ['Le commercial', 'Le prospect', 'Personne'],
        whyChoices: [
            "Le commercial a forcé la vente",
            "Le prospect a été grossier",
            "Aucune bourde — prise de contact respectueuse",
            "Le prospect aurait dû dire oui",
        ],
        whyCorrectIdx: null,
        difficulty: 4,
    },
    {
        id: 'fp-fr-c303',
        story: "Au cabinet, Dr Lefèvre annonce posément un diagnostic difficile à Mme Roland. Il explique les options de traitement, vérifie qu'elle a compris, et propose un nouveau rendez-vous pour répondre aux questions qui viendront chez elle.",
        hasFauxPas: false,
        characters: ['Dr Lefèvre', 'Mme Roland'],
        whoCommitted: null,
        whoChoices: ['Dr Lefèvre', 'Mme Roland', 'Personne'],
        whyChoices: [
            "Dr Lefèvre a été froid",
            "Mme Roland aurait dû demander plus",
            "Aucune bourde — annonce médicale conduite avec soin",
            "Le diagnostic aurait dû être tu",
        ],
        whyCorrectIdx: null,
        difficulty: 4,
    },
    // ============================================================
    // Difficulty 5 — bourdes très subtiles (4 items, 2 fp + 2 ctrl)
    // ============================================================
    {
        id: 'fp-fr-401',
        story: "Lors d'un brief créatif, Yann remercie chaleureusement toute l'équipe en citant chacun par son prénom — sauf Sami, qui a pourtant tenu la nuit blanche pour livrer la maquette. Sami sourit poliment, mais quelque chose s'est éteint dans son regard.",
        hasFauxPas: true,
        characters: ['Yann', 'Sami'],
        whoCommitted: 'Yann',
        whoChoices: ['Yann', 'Sami', 'Personne'],
        whyChoices: [
            "Yann oublie publiquement Sami alors qu'il a porté le travail le plus visible",
            "Sami n'a pas fait sa part",
            "Yann a parlé trop vite",
            "Sami aurait dû se présenter en début de réunion",
        ],
        whyCorrectIdx: 0,
        difficulty: 5,
    },
    {
        id: 'fp-fr-402',
        story: "Au cours d'un dîner, Hélène parle longuement de la beauté de son mariage récent et de la chance qu'elle a eue de le préparer avec sa mère. Sa cousine Inès, dont la mère est décédée l'an dernier, écoute en silence, le sourire poli.",
        hasFauxPas: true,
        characters: ['Hélène', 'Inès'],
        whoCommitted: 'Hélène',
        whoChoices: ['Hélène', 'Inès', 'Personne'],
        whyChoices: [
            "Hélène insiste sur sa mère devant Inès qui vient de perdre la sienne",
            "Inès aurait dû quitter la table",
            "Hélène a trop parlé du mariage",
            "Inès a souri faussement",
        ],
        whyCorrectIdx: 0,
        difficulty: 5,
    },
    {
        id: 'fp-fr-c401',
        story: "À la sortie d'un atelier, deux participants partagent leur ressenti. L'un trouve l'exercice difficile, l'autre l'a aimé. Ils s'écoutent, se disent que c'est intéressant d'avoir des perceptions opposées, et conviennent d'en reparler plus tard.",
        hasFauxPas: false,
        characters: ['Le premier participant', 'Le second participant'],
        whoCommitted: null,
        whoChoices: ['Le premier participant', 'Le second participant', 'Personne'],
        whyChoices: [
            "L'un a invalidé l'autre",
            "Le second a forcé son opinion",
            "Aucune bourde — désaccord assumé et respectueux",
            "Ils auraient dû se mettre d'accord",
        ],
        whyCorrectIdx: null,
        difficulty: 5,
    },
    {
        id: 'fp-fr-c402',
        story: "Lors d'un entretien annuel, Manon partage qu'elle souhaiterait évoluer vers le management. Sa N+1 reconnaît la demande, identifie deux compétences à muscler, et propose un plan sur six mois avec un point d'étape. Manon repart concentrée.",
        hasFauxPas: false,
        characters: ['Manon', 'La N+1'],
        whoCommitted: null,
        whoChoices: ['Manon', 'La N+1', 'Personne'],
        whyChoices: [
            "La N+1 a refusé sans justification",
            "Manon a manqué d'ambition",
            "Aucune bourde — entretien constructif et clair",
            "La N+1 aurait dû promettre la promotion",
        ],
        whyCorrectIdx: null,
        difficulty: 5,
    },
];
/** Filtre items par tier de difficulté. */
export function itemsByDifficulty(difficulty) {
    const clamped = Math.max(1, Math.min(5, Math.round(difficulty)));
    return FAUX_PAS_CORPUS.filter((i) => i.difficulty === clamped);
}
//# sourceMappingURL=corpus.js.map